/**
 * @file 資料をページでめくって読む面 (深さ L1)。
 *
 * @what: 紙 1 ページ = 1 画像を、目次 (見出し → 開始ページ) と ← → でめくる。いまのページが
 *   どの見出しの中かを出し、そのページに載る問 (切り出し済みのもの) と、別冊 (解答例) の
 *   該当ページ、別冊からは本体の問へ行ける。資料どうしの対応づけで自分を指している資料
 *   (過去問・塾技) があれば、このページの語を持つその問 (relatedItemsAt) も並べる。
 * @why: 巻頭の読み物・学校ごとの考査の本文・別冊の解答例は問題ごとの切り出しでは読めない。
 *   資料に pages があればページで読め、items があれば問題集の見せ方になり、両方あれば
 *   切り替える (epic_23 §8)。
 *   ページの上に書いた線 (ページに書き込む) のあるページは、目次の下に「書き込みのあるページ」
 *   として並べ、そこへ戻れる。書き込みは「もう一度解く」で過去の回として残して空にでき、
 *   過去の回は「ページに書き込む」の間だけ切り替えて見られる (hooks/usePageInkAttempts.ts)。
 *   開いたページは端末に覚え、本だなの「つづきから」の手がかりにする (model/resumeReading.ts)。
 *   「見開きで読む」を入れると、横長の画面では2ページを並べ、2ページずつ送る (model/readerSpread.ts)。
 *   組の先のページを「いまのページ」とし、メモ・目次・閲覧位置・選んで聞く・音声はそのページを指す。
 *   書き込みと範囲を囲む間は、そのページだけを1ページで出す。隣のページの書き込みは表示だけする。
 * @failure: 描画のみ。画像の取得失敗はそのページだけエラー表示 (目次と導線は出す)。
 *   URL のページが無ければ、その番号以降で最初にあるページを出す。
 *
 * ページ番号は連番で持ち (`?page=`・保存する値・目次)、画面に出すときだけ資料の
 * `reader_offset` で紙に刷られた番号へ直す。ページ番号の欄に入れた番号も刷られた番号として
 * 受け、連番へ直して開く。※なぜなら読んでいる人が突き合わせるのは手元の紙の番号であり、
 * 問の出典もその番号で書かれているためである。
 */

import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import type { FamilyMember } from "../api/members";
import type { StudyPage } from "../api/pages";
import type { StudyScanItem } from "../api/scans";
import type { StudySource } from "../api/sources";
import { usePageAudio } from "../hooks/usePageAudio";
import { useDeviceSync } from "../hooks/useDeviceSync";
import { usePageInkView } from "../hooks/usePageInkView";
import { retargetDeviceSyncFollowed } from "../lib/deviceSync";
import { useReadingPositionWriter } from "../hooks/useReadingPositions";
import { useScanImage } from "../hooks/useScanImage";
import { prefetchPageImage } from "../lib/pageImageCache";
import { readAudioAutoplay, writeAudioAutoplay } from "../model/readerAudio";
import { writeLastOpenedPage } from "../model/resumeReading";
import {
  nextSpreadPage,
  prevSpreadPage,
  readSpreadEnabled,
  readSpreadShift,
  secondOnLeft,
  spreadOf,
  writeSpreadEnabled,
  writeSpreadShift,
} from "../model/readerSpread";
import {
  FullscreenViewer,
  type ViewerControl,
  type ViewerPanel,
} from "./FullscreenViewer";
import { panelIsOffPlain, useSpreadScreen, wideScreen } from "../lib/wideScreen";
import { HandwritingToolbar } from "./HandwritingToolbar";
import { PageInkLayer } from "./PageInkLayer";
import { PageRectLayer } from "./PageRectLayer";
import {
  BookIcon,
  ClockIcon,
  SelectPageIcon,
  SelectRectIcon,
  SoundIcon,
  SoundStopIcon,
  SparkIcon,
} from "./readerIcons";
import { PageTocPanel } from "./PageTocPanel";
import { ReaderPlainBar } from "./ReaderPlainBar";
import { StudyDialog } from "./StudyDialog";
import { usePageInk } from "../hooks/usePageInk";
import { usePageInkAttempts } from "../hooks/usePageInkAttempts";
import { usePageInkPages } from "../hooks/usePageInkPages";
import { PageInkAttemptChips, PageInkRestartButton } from "./PageInkAttempts";
import { composePageWithInk } from "../lib/handwritingExport";
import { PEN_COLORS, PEN_SIZES, type HandwritingTool } from "../model/handwriting";
import { StudyNotePane } from "./StudyNotePane";
import { DeviceSyncToggle } from "./DeviceSyncToggle";
import { AskBar } from "./AskBar";
import { AnswerPane } from "./AnswerPane";
import {
  ASK_TARGETS_MAX,
  pageRectTarget,
  pageTarget,
  type AskTarget,
} from "../model/askSelection";
import { cropImageToUrl } from "../lib/cropImage";
import type { RelatedPageGroup } from "../model/pageLinks";
import type { ReverseGroup } from "../model/sourceLinks";
import {
  companionPageFor,
  companionsOf,
  itemsOnPage,
  nearestPage,
  printedPageOf,
  readerPageOf,
  tocEntriesAt,
} from "../model/sourcePages";
import "./study.css";

const NO_ATTEMPTS: readonly number[] = [];
const noop = () => {};


export function SourcePageReader({
  familyId,
  source,
  sources,
  pages,
  items,
  wantedPage,
  onGoto,
  onOpenItem,
  relatedItemsAt,
  relatedPagesAt,
  onMakeCard,
  askControls = [],
  backHref,
  onOpenLog,
  panelFooter,
  learnerPersonId,
  learners,
  askTargets = [],
  askQuestion = "",
  onAskQuestionChange,
  askNote = "",
  onAskNoteChange,
  onAsked,
  onAddTarget,
  onToggleTarget,
  onRemoveTarget,
  onClearTargets,
  askCollapsed = false,
  onCollapseAsk,
  onShowAsk,
  weekRange,
  onAudibleChange,
}: {
  familyId: string;
  source: StudySource;
  /** 本だなの全資料 (別冊と本体を引くため)。 */
  sources: readonly StudySource[];
  pages: readonly StudyPage[];
  /** この資料の問 (切り出し済み)。無ければ空。 */
  items: readonly StudyScanItem[];
  /** URL の ?page=。無ければ先頭。 */
  wantedPage: number | null;
  onGoto: (page: number) => void;
  onOpenItem: (itemId: string) => void;
  /** このページの語を持つ、相手の資料の問 (対応づけが無ければ undefined)。 */
  relatedItemsAt?: ((page: number) => ReverseGroup[]) | undefined;
  /** このページに関係する、別の資料のページ (ページの対応が無ければ undefined)。 */
  relatedPagesAt?: ((page: number) => RelatedPageGroup[]) | undefined;
  /** このページを出典に復習カードを作る (無ければボタンを出さない)。 */
  onMakeCard?: ((page: number) => void) | undefined;
  /** ヘッダーに並べる、このページの AI 出力の操作 (AI 解説 / AI 採点)。 */
  askControls?: readonly ViewerControl[];
  /** 「本だなにもどる」の行き先 (省略 = 家族の本だな)。 */
  backHref?: string;
  /** ヘッダーの「記録する」で学習の記録シートを開く (無ければボタンを出さない)。 */
  onOpenLog?: (() => void) | undefined;
  /** 目次パネルの下部に置くリンク (読み方の切り替え・さくいん・問題ごとの解説)。 */
  panelFooter?: ReactNode;
  /** リーダーで選んでいるまなぶ人 (学びメモはこの人のもの)。 */
  learnerPersonId: string | null;
  /** 家族のまなぶ人 (学びメモの付け替えの選択肢)。 */
  learners: readonly FamilyMember[];
  /** 「選んで聞く」で選んでいるもの (質問欄を出さない呼び出しでは省く)。 */
  askTargets?: readonly AskTarget[];
  /** 聞くこと (呼び出し側が持つので、対象を選び直しても消えない)。 */
  askQuestion?: string;
  onAskQuestionChange?: ((value: string) => void) | undefined;
  /** 補足すること (本人が述べたこと)。AI の答えとは別に記録へ残る。 */
  askNote?: string;
  onAskNoteChange?: ((value: string) => void) | undefined;
  /** 聞けたときに呼ぶ (ページの AI 出力の件数を数え直す)。 */
  onAsked?: (() => void) | undefined;
  onAddTarget?: ((target: AskTarget) => void) | undefined;
  /** 押されているものはもう一度押すと外す (ヘッダーの「このページを選ぶ」)。 */
  onToggleTarget?: ((target: AskTarget) => void) | undefined;
  onRemoveTarget?: ((ref: string) => void) | undefined;
  onClearTargets?: (() => void) | undefined;
  /** 選んだものを残したまま、質問の欄をたたんでいるか (「読むだけにもどる」で立つ)。 */
  askCollapsed?: boolean;
  onCollapseAsk?: (() => void) | undefined;
  onShowAsk?: (() => void) | undefined;
  /** 計画の今週の範囲 (連番、閉区間)。目次のパネルに出す。計画に入っていない本では無し。 */
  weekRange?: { from: number; to: number } | undefined;
  /** 付属音声が鳴っているかが変わったら知らせる (読んだ時間の計測が、鳴っている間を操作とみなす)。 */
  onAudibleChange?: ((audible: boolean) => void) | undefined;
}) {
  const nearest = nearestPage(pages, wantedPage ?? 0);
  // サイドペインの切り替え (メモ / 手書き / ページに書き込む / 目次)。広い画面ではメモから始める。
  const [activePanel, setActivePanel] = useState<string | null>(() => (wideScreen() ? "note" : null));
  // ページの上の書き込み。「ページに書き込む」が選ばれている間だけ線を受け、外れている間は表示だけにする。
  const inkMode = activePanel === "pageink";
  // 紙面の範囲を囲んで聞く。ヘッダーの「教材から選ぶ」の群に置き、書き込みとは同時に入らない。
  const [rectMode, setRectMode] = useState(false);
  // 見開きで読む。入り切りは端末ごと、「1ページずらす」は本ごとに端末へ覚える。横長の画面でだけ
  // 2ページを並べ、紙面を触る道具が入っている間は1ページに戻す。※なぜなら書き込みの層・元に戻す操作・
  // 採点はどれもページ単位で作られているためである (代表の判断、2026-10-13)。
  const spreadScreen = useSpreadScreen();
  const [spreadOn, setSpreadOn] = useState(readSpreadEnabled);
  const [spreadShift, setSpreadShift] = useState(() => readSpreadShift(source.id));
  useEffect(() => {
    setSpreadShift(readSpreadShift(source.id));
  }, [source.id]);
  const spreadActive = spreadOn && spreadScreen && !inkMode && !rectMode;
  const spread =
    nearest !== null && spreadActive ? spreadOf(pages, source, nearest.page, spreadShift) : null;
  const current = spread ? (pages.find((p) => p.page === spread.first) ?? nearest) : nearest;
  const currentPageNo = current?.page ?? null;
  const pairPage =
    spread !== null && spread.second !== null
      ? (pages.find((p) => p.page === spread.second) ?? null)
      : null;
  // 画面に出す番号は紙に刷られた番号にする。保存と URL は連番のままにする。
  // ※なぜなら読んでいる人が突き合わせるのは手元の紙の番号であり、自炊で前に付けた表紙の
  //   枚数だけずれた番号を出すと、問の出典や本文の相互参照と合わなくなるためである。
  //   表紙や扉は刷られた番号を持たないので、1未満になる面だけ連番のまま出す。
  const printed = (readerPage: number): number => {
    const no = printedPageOf(source, readerPage);
    return no >= 1 ? no : readerPage;
  };
  const currentPrintedNo = currentPageNo === null ? null : printed(currentPageNo);
  const idx = current ? pages.indexOf(current) : -1;
  const image = useScanImage(current?.image_path ?? null, current?.content_type ?? "image/jpeg");
  const pairImage = useScanImage(pairPage?.image_path ?? null, pairPage?.content_type ?? "image/jpeg");
  // 付属音声を持つ資料だけ操作を出し、開いたページに付く音声を鳴らす。
  const hasAudio = useMemo(() => pages.some((p) => p.audio_path), [pages]);
  const [autoplay, setAutoplay] = useState(readAudioAutoplay);
  const audio = usePageAudio(
    current?.audio_path ?? null,
    current?.audio_content_type ?? "audio/mpeg",
    autoplay,
  );
  const audible = audio.status === "playing";
  useEffect(() => {
    onAudibleChange?.(audible);
    // 呼び出し側の関数は描画のたびに変わるので、鳴っているかどうかだけを依存にする
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audible]);
  const [draft, setDraft] = useState("");
  const navigate = useNavigate();
  const toggleRectMode = () => {
    setRectMode((on) => {
      // 囲みを始めるときは、ページへの書き込みから抜ける (どちらも紙面を触るため)。
      if (!on && activePanel === "pageink") setActivePanel(null);
      return !on;
    });
  };
  // 囲んだところの切り取りと、それを大きく見ているかは分けて持つ。※なぜなら閉じたあとにも
  //   もう一度大きく見られるようにするためである。
  const [crop, setCrop] = useState<{ url: string; label: string } | null>(null);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [cropError, setCropError] = useState<string | null>(null);
  // 囲んだものを選んだものに入れられたか (入らなかった理由) を、モーダルの下に1行で出す。
  const [cropNotice, setCropNotice] = useState<"added" | "already" | "full">("added");
  // 切り取りの URL は、別のものに差し替わるときと unmount のときだけ返す。
  const cropUrl = crop?.url ?? null;
  useEffect(() => {
    return () => {
      if (cropUrl !== null) URL.revokeObjectURL(cropUrl);
    };
  }, [cropUrl]);
  useEffect(() => {
    setCrop(null);
    setZoomOpen(false);
    setCropError(null);
  }, [currentPageNo]);
  // 「メモに写す」で作った1枚 (ページと書き込みを重ねたもの)。メモのペインが受け取って添付する。
  const [attachment, setAttachment] = useState<{ blob: Blob; name: string } | null>(null);
  const [composing, setComposing] = useState(false);
  const [composeError, setComposeError] = useState<string | null>(null);
  // ページを送ったら、作りかけの1枚と失敗の文言は捨てる (別のページのメモに入れない)。
  useEffect(() => {
    setAttachment(null);
    setComposeError(null);
  }, [currentPageNo]);
  const [inkTool, setInkTool] = useState<HandwritingTool>("pen");
  const [inkColor, setInkColor] = useState(PEN_COLORS[0]!.id);
  const [inkSize, setInkSize] = useState(PEN_SIZES[1]!.id);
  // 書き込みのあるページ (目次の下に並べて戻れるようにする)。保存できたページだけ手元の印を直す。
  const {
    pages: inkPages,
    mark: markInkPage,
    attemptsOf,
    addAttempt,
    removeAttempt,
  } = usePageInkPages(familyId, learnerPersonId, source.id);
  const onInkSaved = useCallback(
    (savedPage: number, strokes: number) => markInkPage(savedPage, strokes > 0),
    [markInkPage],
  );
  const pageInk = usePageInk(familyId, learnerPersonId, source.id, currentPageNo, onInkSaved);
  const pairInk = usePageInkView(
    familyId,
    learnerPersonId,
    source.id,
    pairPage?.page ?? null,
    pairPage !== null && inkPages.includes(pairPage.page),
  );
  // 解き直し (過去の回を残す・見る・消す)。「ページに書き込む」の間だけ道具を出す。
  const inkAttempts = usePageInkAttempts({
    familyId,
    learnerId: learnerPersonId,
    sourceId: source.id,
    page: currentPageNo,
    active: inkMode,
    attempts: currentPageNo === null ? NO_ATTEMPTS : attemptsOf(currentPageNo),
    currentDoc: pageInk.doc,
    clearCurrent: pageInk.clearNow,
    addAttempt,
    removeAttempt,
  });
  const viewingPast = inkAttempts.viewing !== null;
  // 紙面に出す線である。過去の回を見ている間はその回の線 (読み終わるまでは空)。
  const shownInkDoc =
    inkAttempts.viewing === null
      ? pageInk.doc
      : (inkAttempts.viewingDoc ?? { ...pageInk.doc, strokes: [] });
  // 「このページを AI 採点」。書きかけを待たずに保存してから、このページを選んだものに足し、質問欄に
  // すぐ採点させる。※なぜならサーバは Storage の線を紙面に重ねて AI へ渡すので、保存が済んでいれば
  // メモに写さなくても採点の材料が揃うためである。
  const [grading, setGrading] = useState(false);
  const [gradeError, setGradeError] = useState<string | null>(null);
  const [autoAsk, setAutoAsk] = useState<{ preset: "ai_grade" } | null>(null);
  const canGradeThisPage =
    onAddTarget !== undefined &&
    learnerPersonId !== null &&
    currentPageNo !== null &&
    !viewingPast &&
    !grading &&
    pageInk.doc.strokes.length > 0 &&
    pageInk.status !== "loading";
  const gradeThisPage = async () => {
    if (!canGradeThisPage || !onAddTarget || currentPageNo === null) return;
    const page = currentPageNo;
    const already = askTargets.some((t) => t.ref === thisPageTarget.ref);
    if (!already && askTargets.length >= ASK_TARGETS_MAX) {
      setGradeError(`選べるのは${ASK_TARGETS_MAX}つまでです。どれかを外してから押してください。`);
      return;
    }
    setGrading(true);
    setGradeError(null);
    const saved = await pageInk.saveNow();
    setGrading(false);
    // 保存を待つ間にページを送っていたら、前のページのことは始めない。
    if (page !== currentPageNo) return;
    if (!saved) {
      setGradeError("書き込みを保存できなかったので、採点を始められません。");
      return;
    }
    onAddTarget(pageTarget(source, page, true));
    onShowAsk?.();
    setAutoAsk({ preset: "ai_grade" });
  };

  // 「メモに写す」は紙面に出ている線 (見ている回) で作る。
  const copyInkToNote = async () => {
    if (image.status !== "ready" || composing) return;
    setComposing(true);
    setComposeError(null);
    try {
      const blob = await composePageWithInk(image.url, shownInkDoc);
      const suffix = inkAttempts.viewing === null ? "" : `-r${inkAttempts.viewing}`;
      setAttachment({ blob, name: `p${currentPageNo ?? 0}${suffix}-ink.png` });
      setActivePanel("note");
    } catch (err) {
      console.error("[study] failed to compose page ink", err);
      setComposeError("メモに写せませんでした。");
    } finally {
      setComposing(false);
    }
  };

  useEffect(() => {
    setDraft(currentPrintedNo === null ? "" : String(currentPrintedNo));
  }, [currentPrintedNo]);

  // 見開きでは組の先のページを「いまのページ」にする。組の後のページを開いて来たときは、URL も
  // 先のページへ直す (履歴は増やさない)。※なぜなら道具を入れて1ページに戻したとき、メモと違うページが
  // 出ないようにするためである。
  const spreadFirst = spread?.first ?? null;
  const spreadSecond = spread?.second ?? null;
  const nearestNo = nearest?.page ?? null;
  useEffect(() => {
    if (spreadFirst !== null && nearestNo !== null && nearestNo !== spreadFirst) onGoto(spreadFirst);
    // 呼び出し側の関数は描画のたびに変わるので、ページの番号だけを依存にする
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spreadFirst, nearestNo]);
  // ほかの端末についていった先が組の後のページなら、ついていった印を組の先のページへ付け替える。
  // 閲覧位置を送る effect より先に置く (同じ描画の中で、先に宣言した effect から走る)。
  useEffect(() => {
    if (spreadFirst !== null && spreadSecond !== null)
      retargetDeviceSyncFollowed(source.id, spreadSecond, spreadFirst);
  }, [source.id, spreadFirst, spreadSecond]);

  // 開いたページを端末に覚え (送れなかったときの手がかり)、まなぶ人の閲覧位置として
  // サーバにも送る (本だなの「つづきから」と「最近開いた本」の正)。
  useEffect(() => {
    if (currentPageNo !== null) writeLastOpenedPage(familyId, source.id, currentPageNo);
  }, [familyId, source.id, currentPageNo]);
  useReadingPositionWriter(familyId, learnerPersonId, source.id, currentPageNo);
  // ほかの端末と同じページを開く連動を入れていれば、ほかの端末で開いた本とページへついていく。
  // 同じ本ならページを送り、別の本ならその本のそのページを開く (別冊の組は model/deviceSync.ts が除く)。
  useDeviceSync({
    familyId,
    learnerPersonId,
    current: { sourceId: source.id, page: currentPageNo },
    sources,
    onFollow: (target) => {
      if (target.sourceId === source.id) onGoto(target.page);
      else navigate(sourceReaderPath(familyId, target.sourceId, target.page));
    },
  });

  const headings = useMemo(
    () => (currentPageNo === null ? [] : tocEntriesAt(source.toc, currentPageNo)),
    [source.toc, currentPageNo],
  );
  // この面に載る問を引く。出典は紙に刷られた番号なので、連番から刷られた番号へ直して突き合わせる。
  const onThisPage = useMemo(
    () => (currentPageNo === null ? [] : itemsOnPage(items, printedPageOf(source, currentPageNo))),
    [items, source, currentPageNo],
  );
  const relatedGroups = useMemo(
    () => (currentPageNo === null || !relatedItemsAt ? [] : relatedItemsAt(currentPageNo)),
    [relatedItemsAt, currentPageNo],
  );
  const relatedPages = useMemo(
    () => (currentPageNo === null || !relatedPagesAt ? [] : relatedPagesAt(currentPageNo)),
    [relatedPagesAt, currentPageNo],
  );
  const companions = useMemo(() => companionsOf(sources, source.id), [sources, source.id]);
  const parent = source.companion_of
    ? (sources.find((s) => s.id === source.companion_of) ?? null)
    : null;

  // 本に答えが引けるか (別冊の、この見出しのページ)。引けるときだけ「答えを見る」を出す。
  // 答えの列は開いたままページを送れ、送った先の見出しの答えに切り替わる。
  const answerAt = useMemo(() => {
    for (const c of companions) {
      for (const h of headings) {
        const page = companionPageFor(c, h.title);
        if (page !== null) return { source: c, page };
      }
    }
    return null;
  }, [companions, headings]);
  const [answerOpen, setAnswerOpen] = useState(false);
  // 答えの列はパネル (メモ・手書き・目次) と同じ場所に出すので、開くときはパネルを閉じる。
  // 「ページに書き込む」は列を使わないので残す。※なぜなら答えを見ながら丸を付けるためである。
  const toggleAnswer = () => {
    if (!answerOpen && activePanel !== "pageink") setActivePanel(null);
    setAnswerOpen(!answerOpen);
  };
  // いま開いている紙面に線があるか。「このページを選ぶ」で選んだものに「AI 採点」を出す根拠になる。
  const pageInked = currentPageNo !== null && pageInk.doc.strokes.length > 0;
  const thisPageTarget = useMemo(
    () => pageTarget(source, currentPageNo ?? 0, pageInked),
    [source, currentPageNo, pageInked],
  );

  const gotoIndex = (next: number) => {
    const target = pages[Math.max(0, Math.min(pages.length - 1, next))];
    if (target) onGoto(target.page);
  };
  // 見開きのときは2ページずつ送る (前へ送った先は、その組の先のページへ直る)。
  const prevPage = spread ? prevSpreadPage(pages, spread) : null;
  const nextPage = spread ? nextSpreadPage(pages, spread) : null;
  const gotoPrev = () => {
    if (!spread) gotoIndex(idx - 1);
    else if (prevPage !== null) onGoto(prevPage);
  };
  const gotoNext = () => {
    if (!spread) gotoIndex(idx + 1);
    else if (nextPage !== null) onGoto(nextPage);
  };
  const hasPrev = spread ? prevPage !== null : idx > 0;
  const hasNext = spread ? nextPage !== null : idx < pages.length - 1;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) return;
      if (e.key === "ArrowLeft") gotoPrev();
      if (e.key === "ArrowRight") gotoNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // gotoPrev / gotoNext は pages / onGoto / 見開きの組から作る (依存は位置と組で足りる)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, pages.length, onGoto, spreadFirst, spreadSecond]);

  // 隣のページを先読みして、めくりを取得待ちなしにする (進む方向を優先)。
  // 続けて送っても取得が追いつくよう、進む方向は4枚先まで取る。デコードして保持するのは
  // 次・その次・前の3枚だけにする。※なぜならデコードした画像は1枚で数 MB のメモリを使うためである。
  // 見開きでは次の組と前の組の2枚ずつをデコードし、その先の1組を取るだけにする。
  const lastIdx = pairPage ? pages.indexOf(pairPage) : idx;
  const spreading = spread !== null;
  useEffect(() => {
    if (idx < 0) return;
    const order = spreading
      ? [lastIdx + 1, lastIdx + 2, idx - 1, idx - 2, lastIdx + 3, lastIdx + 4]
      : [idx + 1, idx + 2, idx - 1, idx + 3, idx + 4];
    const decodeUntil = spreading ? lastIdx + 2 : idx + 2;
    for (const n of order) {
      const p = pages[n];
      if (p?.image_path) {
        prefetchPageImage(p.image_path, p.content_type ?? "image/jpeg", { decode: n <= decodeUntil });
      }
    }
  }, [idx, lastIdx, spreading, pages]);

  const submitPage = (e: FormEvent) => {
    e.preventDefault();
    const n = Number(draft);
    if (Number.isInteger(n) && n >= 1) onGoto(readerPageOf(source, n));
  };

  if (!current) {
    return (
      <p className="study-muted">
        この資料にはまだページがありません。ページを取り込むとここで読めるようになります。
      </p>
    );
  }

  const sourceHref = (id: string, query = "") =>
    `/study/${encodeURIComponent(familyId)}/sources/${encodeURIComponent(id)}${query}`;

  const pageForm = (
    <form className="study-viewer-pageform" onSubmit={submitPage}>
      <label className="study-viewer-pageform-label">
        <span>p.</span>
        <input
          type="number"
          inputMode="numeric"
          min={1}
          max={printed(pages[pages.length - 1]?.page ?? 1)}
          value={draft}
          aria-label="開くページ番号"
          onChange={(e) => setDraft(e.target.value)}
        />
      </label>
      <button type="submit" className="study-viewer-btn study-viewer-btn--small">
        移動
      </button>
      <span className="study-viewer-pageform-total">
        {source.page_count ? `全 ${source.page_count} ページ` : `${pages.length} ページ`}
      </span>
      <DeviceSyncToggle className="study-device-sync--reader" />
      {spreadScreen ? (
        <label className="study-viewer-check">
          <input
            type="checkbox"
            checked={spreadOn}
            onChange={(e) => {
              setSpreadOn(e.target.checked);
              writeSpreadEnabled(e.target.checked);
            }}
          />
          <span>見開きで読む</span>
        </label>
      ) : null}
      {spreadScreen && spreadOn ? (
        <button
          type="button"
          className="study-viewer-btn study-viewer-btn--small"
          aria-pressed={spreadShift}
          onClick={() => {
            const next = !spreadShift;
            setSpreadShift(next);
            writeSpreadShift(source.id, next);
          }}
        >
          1ページずらす
        </button>
      ) : null}
    </form>
  );

  // メモのペインは切り替えても消さない (手書きの送信や自動保存の途中で unmount しないため)。
  const notePane = (
    <StudyNotePane
      familyId={familyId}
      source={source}
      sources={sources}
      page={current.page}
      learnerPersonId={learnerPersonId}
      learners={learners}
      view={activePanel === "ink" ? "ink" : "text"}
      attachment={attachment}
      onAttached={() => setAttachment(null)}
      {...(onAddTarget ? { onSelectTarget: onAddTarget } : {})}
    />
  );
  const selectRect = async (rect: { x: number; y: number; w: number; h: number }) => {
    const page = current.page;
    const target = pageRectTarget(source, page, rect, pageInked);
    // 足せたか (上限に達していれば足せない) を見て、案内の文言を変える。
    const already = askTargets.some((t) => t.ref === target.ref);
    const full = !already && askTargets.length >= ASK_TARGETS_MAX;
    onAddTarget?.(target);
    setCropNotice(full ? "full" : already ? "already" : "added");
    if (image.status !== "ready") return;
    setCropError(null);
    try {
      const url = await cropImageToUrl(image.url, rect);
      // 切り取っている間にページを送っていたら、前のページのものは出さない。
      if (page !== currentPageNo) {
        URL.revokeObjectURL(url);
        return;
      }
      setCrop({ url, label: `${source.title} p.${printedPageOf(source, page)}（囲んだところ）` });
      setZoomOpen(true);
    } catch (err) {
      console.error("[study] failed to crop the page", err);
      setCropError("囲んだところを大きくできませんでした。");
    }
  };

  // ヘッダーの操作である。狭い画面では primary のものだけが表に残り、ほかは⋯のメニューへ入る
  // (代表の判断、2026-09-11)。「教材から選ぶ」は「範囲を囲む」を先に置いて表に残し、
  // 「このページを選ぶ」は⋯へ入れる (代表の判断、2026-09-12)。※なぜなら聞く対象は紙面の一部が
  // 中心で、ページ全体はその外側に当たるためである。キーボードだけのときと、ページ全体について
  // 聞くときの道は「このページを選ぶ」に残す (囲む操作はなぞるしか持たない)。
  const headerControls: ViewerControl[] = [
    ...(onToggleTarget && onAddTarget
      ? [
          {
            id: "pick-rect",
            label: "範囲を囲む",
            icon: <SelectRectIcon />,
            group: "教材から選ぶ",
            tone: "pick" as const,
            primary: true,
            pressed: rectMode,
            onSelect: toggleRectMode,
          },
        ]
      : []),
    ...(onToggleTarget
      ? [
          {
            id: "pick-page",
            label: "このページを選ぶ",
            icon: <SelectPageIcon />,
            group: "教材から選ぶ",
            tone: "pick" as const,
            pressed: askTargets.some((t) => t.ref === thisPageTarget.ref),
            onSelect: () => onToggleTarget(thisPageTarget),
          },
        ]
      : []),
    ...(hasAudio
      ? [
          {
            id: "audio-play",
            label: audio.status === "playing" ? "止める" : "音声を聞く",
            ariaLabel:
              audio.status === "playing"
                ? "このページの音声を止める"
                : "このページの音声を聞く",
            icon: audio.status === "playing" ? <SoundStopIcon /> : <SoundIcon />,
            group: "音声",
            tone: "quiet" as const,
            primary: true,
            pressed: audio.status === "playing",
            onSelect: () => (audio.status === "playing" ? audio.stop() : audio.play()),
          },
          {
            id: "audio-auto",
            label: "自動で再生",
            ariaLabel: "ページを開いたら音声を自動で再生する",
            group: "音声",
            tone: "quiet" as const,
            pressed: autoplay,
            onSelect: () => {
              const next = !autoplay;
              setAutoplay(next);
              writeAudioAutoplay(next);
              // 入にした操作そのものを起点に鳴らす (ブラウザはここで再生を許す)。
              if (next) audio.play();
              else audio.stop();
            },
          },
        ]
      : []),
    ...askControls,
    ...(answerAt !== null || answerOpen
      ? [
          {
            id: "answer",
            label: "答えを見る",
            ariaLabel: "本の答えを見る",
            icon: <BookIcon />,
            group: "この本の操作",
            tone: "quiet" as const,
            pressed: answerOpen,
            onSelect: toggleAnswer,
          },
        ]
      : []),
    ...(onOpenLog
      ? [
          {
            id: "log",
            label: "記録する",
            ariaLabel: `${source.title} の学習を記録する`,
            icon: <ClockIcon />,
            group: "この本の操作",
            tone: "quiet" as const,
            onSelect: onOpenLog,
          },
        ]
      : []),
  ];

  // 音声の様子を1行で出す。落ち着いているとき (止まっている / 音声を持たない資料) は出さない。
  const audioNotice = !hasAudio
    ? null
    : audio.status === "error"
      ? "音声を読み込めませんでした。ページを開き直すと取り直します。"
      : audio.status === "blocked"
        ? "「音声を聞く」を押すと鳴ります。"
        : audio.status === "loading"
          ? "音声を読み込んでいます..."
          : audio.status === "playing"
            ? "音声を再生しています。"
            : audio.status === "none"
              ? "このページに音声はありません。"
              : null;

  const panels: ViewerPanel[] = [
    { id: "note", label: "メモ", primary: true },
    { id: "ink", label: "手書き" },
    ...(pageInk.enabled ? [{ id: "pageink", label: "ページに書き込む", kind: "mode" as const }] : []),
    {
      id: "toc",
      label: "目次",
      content: (
        <PageTocPanel
          source={source}
          currentPage={current.page}
          headings={headings}
          onThisPage={onThisPage}
          relatedGroups={relatedGroups}
          relatedPages={relatedPages}
          companions={companions}
          parent={parent}
          onGoto={onGoto}
          onOpenItem={onOpenItem}
          sourceHref={sourceHref}
          onMakeCard={onMakeCard}
          inkPages={inkPages}
          weekRange={weekRange}
          footer={panelFooter}
        />
      ),
    },
  ];

  return (
    <>
      {crop !== null && zoomOpen ? (
        <StudyDialog
          title={crop.label}
          labelledBy="study-page-zoom-title"
          onClose={() => setZoomOpen(false)}
          size="wide"
        >
          <img src={crop.url} alt={crop.label} className="study-dialog-img" />
          <p className="study-dialog-more">
            {cropNotice === "full"
              ? `選べるのは${ASK_TARGETS_MAX}つまでです。どれかを外してから囲み直してください。`
              : cropNotice === "already"
                ? "この範囲は、すでに選んだものに入っています。"
                : "この範囲を選んだものに入れました。下の欄から聞けます。"}
          </p>
        </StudyDialog>
      ) : null}
    <FullscreenViewer
      immersive
      paperTool={rectMode || inkMode}
      image={image}
      alt={`p.${printed(current.page)}`}
      label={source.title}
      menuExtra={pageForm}
      plainAction={
        <ReaderPlainBar
          offPlain={
            panelIsOffPlain(activePanel) ||
            (answerOpen && !wideScreen()) ||
            rectMode ||
            (askTargets.length > 0 && !askCollapsed)
          }
          onPlain={() => {
            setActivePanel(null);
            setAnswerOpen(false);
            setRectMode(false);
            onCollapseAsk?.();
          }}
          askCount={askCollapsed ? askTargets.length : 0}
          onShowAsk={() => onShowAsk?.()}
          onClearAsk={() => onClearTargets?.()}
        />
      }
      controls={headerControls}
      closeLabel="本だなにもどる"
      shortCloseLabel="本だな"
      onClose={() => navigate(backHref ?? `/study/${encodeURIComponent(familyId)}?view=shelf`)}
      onPrev={gotoPrev}
      onNext={gotoNext}
      hasPrev={hasPrev}
      hasNext={hasNext}
      {...(pairPage
        ? {
            spread: {
              image: pairImage,
              alt: `p.${printed(pairPage.page)}`,
              onLeft: secondOnLeft(source),
              overlay:
                pairInk !== null ? (
                  <PageInkLayer
                    doc={pairInk}
                    onStroke={noop}
                    onAspect={noop}
                    tool={inkTool}
                    colorId={inkColor}
                    sizeId={inkSize}
                    enabled={false}
                  />
                ) : null,
            },
          }
        : {})}
      panels={panels}
      activePanelId={activePanel}
      onPanelChange={(id) => {
        if (id === "pageink") setRectMode(false);
        // パネルの列を開くなら答えの列は閉じる (同じ場所に出すため)。
        else if (id !== null) setAnswerOpen(false);
        setActivePanel(id);
      }}
      persistentPane={{ visibleFor: ["note", "ink"], content: notePane }}
      {...(answerOpen
        ? {
            sidePane: (
              <AnswerPane
                familyId={familyId}
                answer={answerAt}
                onClose={() => setAnswerOpen(false)}
              />
            ),
          }
        : {})}
      subBar={
        rectMode ? (
          <div className="study-hw-toolbar">
            <span className="study-viewer-ink-state">
              紙面をなぞって囲みます。ページ全体は「このページを選ぶ」です
            </span>
            {cropError !== null ? (
              <span className="study-viewer-ink-state study-viewer-ink-error" role="alert">
                {cropError}
              </span>
            ) : null}
            {crop !== null && !zoomOpen ? (
              <button
                type="button"
                className="study-viewer-btn study-viewer-btn--small"
                onClick={() => setZoomOpen(true)}
              >
                囲んだところを大きく見る
              </button>
            ) : null}
          </div>
        ) : inkMode && pageInk.enabled ? (
          <div className="study-hw-toolbar">
            {viewingPast ? null : (
              <HandwritingToolbar
                tool={inkTool}
                colorId={inkColor}
                sizeId={inkSize}
                onTool={setInkTool}
                onColor={setInkColor}
                onSize={setInkSize}
                canUndo={pageInk.canUndo && !inkAttempts.busy}
                canRedo={pageInk.canRedo && !inkAttempts.busy}
                canClear={pageInk.canUndo && !inkAttempts.busy}
                onUndo={pageInk.undo}
                onRedo={pageInk.redo}
                onClear={pageInk.clear}
                afterClear={
                  <PageInkRestartButton
                    nextAttempt={inkAttempts.nextAttempt}
                    disabled={!inkAttempts.canStartNew}
                    limitReached={inkAttempts.limitReached}
                    busy={inkAttempts.busy}
                    onRestart={() => void inkAttempts.startNew()}
                  />
                }
              />
            )}
            <PageInkAttemptChips
              attempts={currentPageNo === null ? NO_ATTEMPTS : attemptsOf(currentPageNo)}
              savedAt={inkAttempts.savedAt}
              viewing={inkAttempts.viewing}
              busy={inkAttempts.busy}
              onView={inkAttempts.view}
              onRemove={(n) => void inkAttempts.remove(n)}
            />
            <span
              className={`study-viewer-ink-state${inkAttempts.error !== null || gradeError !== null ? " study-viewer-ink-error" : ""}`}
              role={inkAttempts.error !== null || gradeError !== null ? "alert" : "status"}
            >
              {inkAttempts.error ??
                composeError ??
                gradeError ??
                (viewingPast
                  ? `${inkAttempts.viewing}回目の書き込みです (見るだけ)。紙面に触れると「いま」に戻ります`
                  : pageInk.status === "saving"
                    ? "保存しています..."
                    : pageInk.status === "error"
                      ? "保存できませんでした"
                      : pageInk.status === "saved"
                        ? "保存しました"
                        : "")}
            </span>
            <button
              type="button"
              className="study-viewer-btn study-viewer-btn--small study-viewer-copy-btn"
              disabled={composing || shownInkDoc.strokes.length === 0 || image.status !== "ready"}
              onClick={() => void copyInkToNote()}
            >
              {composing ? "メモに入れています..." : "メモに写す"}
            </button>
            {onAddTarget ? (
              <button
                type="button"
                className="study-viewer-btn study-viewer-btn--small study-viewer-btn--ai"
                disabled={!canGradeThisPage}
                title={
                  learnerPersonId === null
                    ? "まなぶ人を選ぶと聞けます"
                    : viewingPast
                      ? "「いま」に戻ると採点できます"
                      : pageInk.doc.strokes.length === 0
                        ? "紙面に書いてから押してください"
                        : undefined
                }
                onClick={() => void gradeThisPage()}
              >
                <SparkIcon />
                {grading ? "保存しています..." : "このページを AI 採点"}
              </button>
            ) : null}
          </div>
        ) : audioNotice !== null ? (
          <div className="study-hw-toolbar">
            <span
              className={`study-viewer-ink-state${audio.status === "error" ? " study-viewer-ink-error" : ""}`}
              role={audio.status === "error" ? "alert" : "status"}
            >
              {audioNotice}
            </span>
          </div>
        ) : null
      }
      bottomBar={
        askTargets.length > 0 &&
        !askCollapsed &&
        onAskQuestionChange &&
        onAskNoteChange &&
        onRemoveTarget &&
        onClearTargets ? (
          <AskBar
            familyId={familyId}
            learnerPersonId={learnerPersonId}
            targets={askTargets}
            question={askQuestion}
            onQuestionChange={onAskQuestionChange}
            note={askNote}
            onNoteChange={onAskNoteChange}
            onRemove={onRemoveTarget}
            onClear={onClearTargets}
            onAsked={onAsked}
            autoAsk={autoAsk}
            onAutoAskDone={() => setAutoAsk(null)}
          />
        ) : null
      }
      overlay={
        <>
          <PageRectLayer enabled={rectMode} onSelect={(rect) => void selectRect(rect)} />
          {pageInk.enabled ? (
            <PageInkLayer
              doc={shownInkDoc}
              onStroke={pageInk.push}
              onAspect={pageInk.setAspect}
              tool={inkTool}
              colorId={inkColor}
              sizeId={inkSize}
              enabled={inkMode && !inkAttempts.busy}
              viewingPast={viewingPast}
              onLeavePast={() => inkAttempts.view(null)}
            />
          ) : null}
        </>
      }
    />
    </>
  );
}

/** 本を読む画面の、そのページを開いた URL である。 */
function sourceReaderPath(familyId: string, sourceId: string, page: number): string {
  return `/study/${encodeURIComponent(familyId)}/sources/${encodeURIComponent(sourceId)}?page=${page}`;
}
